import { Box, IconButton, Stack } from "@mui/material";
import React, { useRef, useEffect, useState, useCallback } from "react";
import { CustomTextView } from "../../../component/CustomTextView";
import { useTranslation } from "react-i18next";
import { DeleteRounded, RedoRounded, UndoRounded } from "@mui/icons-material";
import { COLOR_BLACK, COLOR_WHITE } from "../../../../data/colors/Colors";
import { CustomCardView } from "../../../component/CustomCardView";
import { ReactSketchCanvas } from "react-sketch-canvas";
import useWindowDimensions from "../../../../core/utils/UseWindowDimensions";

export const BoardView = ({
  onDrawEnded,
}: {
  onDrawEnded: (data: string) => void;
}) => {
  const canvasRef = useRef(null);

  const [brushColor, setBrushColor] = useState("black");
  const [brushSize, setBrushSize] = useState<number>(5);

  const colors = [
    "black",
    "yellow",
    "red",
    "blue",
    "green",
    "brown",
    "gray",
    "purple",
    "white",
    "lightBlue",
    "orange",
    "pink",
  ];

  useEffect(() => {
    let interval = setInterval(async () => {
      if (!canvasRef.current) return;
      const data = await (canvasRef.current as any).exportImage();
      onDrawEnded(data);
    }, 1000);

    return () => {
      clearTimeout(interval);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);
  const { width } = useWindowDimensions();

  const styles = {
    border: "2px solid black",
    borderRadius: 2,
  };
  const isMobile = width < 900;
  return (
    <Stack gap={1} width={"100%"} alignItems={"center"}>
      <ReactSketchCanvas
        ref={canvasRef}
        style={styles}
        width={"90vw"}
        height={"60vh"}
        strokeWidth={brushSize}
        strokeColor={brushColor}
      />
      <CustomCardView
        padding={isMobile ? 1 : 2}
        borderRadius={4}
        children={
          <Stack gap={2} width={"100%"}>
            <Stack
              alignItems={"center"}
              gap={isMobile ? 0.5 : 1}
              direction={"row"}
              sx={{ direction: "ltr" }}
            >
              {colors.map((color) => {
                return (
                  <SelectColorView
                    color={color}
                    isSelected={brushColor == color}
                    onClick={() => setBrushColor(color)}
                  />
                );
              })}
            </Stack>
            <Stack
              direction={"row"}
              alignItems={"center"}
              gap={isMobile ? 1 : 4}
              justifyContent={"space-between"}
              sx={{ direction: "ltr" }}
            >
              <IconButton
                sx={{ border: "1px solid black" }}
                onClick={() => {
                  if (!canvasRef.current) return;
                  (canvasRef.current as any).resetCanvas();
                }}
              >
                <DeleteRounded sx={{ color: COLOR_BLACK }} />
              </IconButton>

              <Stack direction={"row"} gap={isMobile ? 0.5 : 1}>
                <IconButton
                  sx={{ border: "1px solid black" }}
                  onClick={() => {
                    if (!canvasRef.current) return;
                    (canvasRef.current as any).undo();
                  }}
                >
                  <UndoRounded sx={{ color: COLOR_BLACK }} />
                </IconButton>

                <IconButton
                  sx={{ border: "1px solid black" }}
                  onClick={() => {
                    if (!canvasRef.current) return;
                    (canvasRef.current as any).redo();
                  }}
                >
                  <RedoRounded sx={{ color: COLOR_BLACK }} />
                </IconButton>
              </Stack>

              <Stack alignItems={"center"}>
                <input
                  type="range"
                  color="#000"
                  min="1"
                  max="100"
                  value={brushSize}
                  onChange={(e) => setBrushSize(Number(e.target.value))}
                />

                <Stack gap={0.5} direction={"row"} alignItems={"center"}>
                  <CustomTextView text={"Size:"} variant="subtitle2" />
                  <CustomTextView
                    text={brushSize.toString()}
                    variant="subtitle2"
                  />
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        }
      />
    </Stack>
  );
};

function SelectColorView({
  color,
  isSelected,
  onClick,
}: {
  color: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  const { width } = useWindowDimensions();
  const isMobile = width < 900;
  return (
    <IconButton
      sx={{ p: 0, border: isSelected ? "2px solid grey" : undefined }}
      onClick={onClick}
    >
      <Box
        width={isMobile ? 26 : 40}
        height={isMobile ? 26 : 40}
        sx={{
          bgcolor: color,
          borderRadius: 80,
          m: isSelected ? 0.2 : 0,
          flexShrink: 0,
        }}
      />
    </IconButton>
  );
}
